import { ErrorHandler, Injectable } from "@angular/core";
import axios, { AxiosInstance } from "axios";
import { applyFreshAuthHeaders } from "src/app/utils/axios-auth-headers";
import { handleUnauthorizedRedirect } from "src/app/utils/unauthorized-handler";
import { refreshWso2TokenOnce } from "src/app/utils/wso2-token-refresh";
import { environment } from "src/environments/environment";

export interface Params {
  [key: string]: any;
}

export interface GetOptions {
  url: string;
  params?: any;
}

export interface PostOptions {
  url: string;
  body?: Params;
}

export interface ErrorResponse {
  success: boolean;
  message: string;
  status: number;
}

@Injectable({ providedIn: "root" })
export class CoreService {
  private http: AxiosInstance;
  private errorHandler: ErrorHandler;

  constructor(errorHandler: ErrorHandler) {
    this.errorHandler = errorHandler;

    this.http = axios.create({
      baseURL: environment.othersUrl.core,
      timeout: 100000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.http.interceptors.request.use(
      (req) => {
        applyFreshAuthHeaders(req);
        this.http.defaults.headers.common["Authorization"] =
          req.headers.common["Authorization"];
        if (req.headers.common["kc-auth"]) {
          this.http.defaults.headers.common["kc-auth"] =
            req.headers.common["kc-auth"];
        }
        return req;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.http.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const config = error?.config;
        if (status === 401 && config && !config.__retry401) {
          config.__retry401 = true;
          try {
            const newToken = await refreshWso2TokenOnce();
            config.headers = config.headers || {};
            config.headers["Authorization"] = "Bearer " + newToken;
            this.http.defaults.headers.common["Authorization"] =
              "Bearer " + newToken;
            return this.http.request(config);
          } catch (refreshError) {
            handleUnauthorizedRedirect();
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   *  Para métodos via GET
   */
  public async get<T>(options: GetOptions): Promise<T> {
    try {
      const response = await this.http.request<T>({
        method: "get",
        url: options.url,
        params: options.params,
      });

      return response.data;
    } catch (error) {
      return Promise.reject(this.managerError(error));
    }
  }

  /**
   *  Para métodos via POST
   */
  public async post<T>(url: string, body: any, options?: any): Promise<T> {
    try {
      const response = await this.http.request<T>({
        method: "post",
        url: url,
        data: body,
        responseType: options?.responseType,
      });

      return response.data;
    } catch (error) {
      return Promise.reject(this.managerError(error));
    }
  }

  public async postWithBody<T>(url: string, body: any): Promise<T> {
    try {
      const response = await this.http.request<T>({
        method: "post",
        url: url,
        data: body,
      });

      return response.data;
    } catch (error) {
      return Promise.reject(this.managerError(error));
    }
  }

  /**
   *  Para métodos via PUT
   */
  public async put<T>(id: number, url: string, body: any): Promise<T> {
    try {
      const response = await this.http.request<T>({
        method: "put",
        url: `${url}/${id}`,
        data: body,
      });

      return response.data;
    } catch (error) {
      return Promise.reject(this.managerError(error));
    }
  }

  /**
   *  Para métodos via PATCH
   */
  public async patch<T>(id: any, url: string, body: any): Promise<T> {
    try {
      const response = await this.http.request<T>({
        method: "patch",
        url: `${url}/${id}`,
        data: body,
      });

      return response.data;
    } catch (error) {
      return Promise.reject(this.managerError(error));
    }
  }

  /**
   *  Para métodos via DELETE
   */
  public async delete<T>(id: any, url: string): Promise<T> {
    try {
      const response = await this.http.request<T>({
        method: "delete",
        url: `${url}/${id}`,
      });

      return response.data;
    } catch (error) {
      return Promise.reject(this.managerError(error));
    }
  }

  /**
   * Gerenciador de erros padrão (interceptador)
   */
  private managerError(error: any): ErrorResponse {
    this.errorHandler.handleError(error);
    const {
      response: { data = null, status },
    } = error;

    if (status === 401) {
      handleUnauthorizedRedirect();
    }

    if (data) {
      return data;
    }
    return {
      success: false,
      status,
      message: "Ocorreu um erro na requisição com o servidor",
    };
  }
}
